import React, { useMemo } from 'react';
import { Navigate, NavLink, Outlet, Route, Routes, useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { Link2 } from 'lucide-react';
import {
  PartnerDeskProvider,
  PartnerDeskRoutes,
  brandStyle,
  capabilitySet,
  deskNavItems,
  useDesk,
  useDeskData,
  type DeskCapabilities,
  type DeskNavItem,
  type DeskProgram,
} from '@valutten/partner-desk-ui';
import { Skeleton } from '../../../components/common/SkeletonLoader';
import { createTenantDeskApi, useDeskProgramDetails } from '../../../hooks/usePartnerDesk';
import { MY_PROGRAM_PATH, tenantDeskUiCapabilities, type DeskProgramRef } from '../../../utils/partnerReferrals';
import type { MyProgramOutletContext } from '../portalContext';
import { DESK_SLOTS } from './deskSlots';
import LoanLinksPage from './LoanLinksPage';

/**
 * "My referral program" (docs/plans/2026-10-23-WEBSITE-FORMS-PLATFORM.md §16):
 * the shared desk (@valutten/partner-desk-ui) mounted under Partner Referrals
 * for a referral program this company operates, on the tenant door. Mounted
 * at /partner-referrals/program/* ; each program is at
 * /partner-referrals/program/:partnerId/* .
 *
 * Capabilities: the server's list from partnerDeskGetContext intersected with
 * the shared role table (super_admin and administrator: the full desk with
 * fee cards, payouts, settings, loan links and the CRM stage map; marketing:
 * verify, invite, reject, resolve duplicates and move stages). The server
 * checks every call again.
 */

/**
 * Inside VALUTTEN the desk takes the app's own accent (bg-blue-600), not the
 * program's brand colour: a program brand (Cavara's is black) is chosen for
 * the light partner portal and can vanish on the dark client shell.
 */
const VALUTTEN_ACCENT = '#2563eb';

export function programBasePath(partnerId: string): string {
  return `${MY_PROGRAM_PATH}/${encodeURIComponent(partnerId)}`;
}

const DeskSkeleton: React.FC = () => (
  <div className="space-y-3" aria-busy="true">
    <Skeleton className="h-10 w-full" />
    <Skeleton className="h-64 w-full" />
  </div>
);

/** The desk's own section nav (plus Loan links for crm.link) and the program switcher. */
const DeskShell: React.FC<{ programs: DeskProgramRef[]; partnerId: string }> = ({ programs, partnerId }) => {
  const { capabilities, paths, can } = useDesk();
  const { overdueCount } = useDeskData();
  const navigate = useNavigate();
  const items: DeskNavItem[] = deskNavItems(capabilities, paths, overdueCount);
  if (can('crm.link')) items.push({ to: `${paths.root}/loan-links`, label: 'Loan links', icon: Link2 });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        {programs.length > 1 && (
          <label className="flex items-center gap-2 text-sm text-on-surface-variant">
            <span>Program</span>
            <select
              className="rounded-card-xs border border-on-surface/15 bg-surface-raised px-2 py-1.5 text-sm text-on-surface"
              value={partnerId}
              onChange={(e) => navigate(programBasePath(e.target.value))}
            >
              {programs.map((p) => (
                <option key={p.partnerId} value={p.partnerId}>
                  {p.name}
                </option>
              ))}
            </select>
          </label>
        )}
        <nav aria-label="Referral program" className="flex flex-wrap gap-1">
          {items.map(({ to, label, icon: Icon, end, badge }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) =>
                `flex items-center gap-2 whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                  isActive ? 'bg-surface-raised text-on-surface shadow-sm ring-1 ring-on-surface/10' : 'text-on-surface-variant hover:bg-on-surface/5'
                }`
              }
            >
              <Icon className="h-4 w-4" aria-hidden="true" />
              <span>{label}</span>
              {badge ? (
                <span className="rounded-full bg-red-600 px-1.5 text-xs font-semibold text-white" aria-label={`${badge} overdue`}>
                  {badge}
                </span>
              ) : null}
            </NavLink>
          ))}
        </nav>
      </div>
      <Outlet />
    </div>
  );
};

const NotAllowed: React.FC<{ to: string }> = ({ to }) => <Navigate to={to} replace />;

/** One program's desk. Keyed by partnerId by its parent, so switching programs starts clean. */
export const ProgramDesk: React.FC<{
  program: DeskProgramRef;
  programs: DeskProgramRef[];
  capabilities: DeskCapabilities;
}> = ({ program: ref, programs, capabilities }) => {
  const api = useMemo(() => createTenantDeskApi(ref.partnerId), [ref.partnerId]);
  const details = useDeskProgramDetails(ref.partnerId);
  const program = useMemo<DeskProgram>(
    () => ({ name: ref.name, vocabulary: details.vocabulary, rewards: details.rewards, firstContactHours: details.firstContactHours }),
    [ref.name, details.vocabulary, details.rewards, details.firstContactHours]
  );
  const viewer = useMemo(() => ({ memberId: null }), []);
  const basePath = programBasePath(ref.partnerId);

  if (details.loading) return <DeskSkeleton />;
  const shell = <DeskShell programs={programs} partnerId={ref.partnerId} />;
  return (
    <div
      className="rounded-2xl border border-gray-700 bg-surface p-4 text-on-surface [color-scheme:dark] sm:p-6"
      style={brandStyle(VALUTTEN_ACCENT) as React.CSSProperties}
    >
      <PartnerDeskProvider api={api} capabilities={capabilities} program={program} viewer={viewer} basePath={basePath} slots={DESK_SLOTS}>
        <Routes>
          <Route element={shell}>
            <Route
              path="loan-links"
              element={capabilities.has('crm.link') ? <LoanLinksPage partnerId={ref.partnerId} /> : <NotAllowed to={basePath} />}
            />
          </Route>
          <Route path="*" element={<PartnerDeskRoutes layout={shell} />} />
        </Routes>
      </PartnerDeskProvider>
    </div>
  );
};

const ProgramRoute: React.FC<{ programs: DeskProgramRef[]; capabilities: DeskCapabilities }> = ({ programs, capabilities }) => {
  const { partnerId } = useParams();
  const program = programs.find((p) => p.partnerId === partnerId);
  if (!program) return <Navigate to={MY_PROGRAM_PATH} replace />;
  return <ProgramDesk key={program.partnerId} program={program} programs={programs} capabilities={capabilities} />;
};

const MyReferralProgramPage: React.FC = () => {
  const ctx = useOutletContext<MyProgramOutletContext | null>();
  const my = ctx?.myProgram;
  const capabilities = useMemo(
    () => capabilitySet(tenantDeskUiCapabilities(my?.role, my?.desk.capabilities, my?.hideCommissions ?? true)),
    [my?.role, my?.desk.capabilities, my?.hideCommissions]
  );

  if (my && my.desk.status === 'loading') return <DeskSkeleton />;
  // Not a desk role, no add-on, no program, or the context call failed: back to Partner Referrals.
  if (!my || !my.hasProgram) return <Navigate to="/partner-referrals" replace />;
  const programs = my.desk.programs;
  return (
    <Routes>
      <Route index element={<Navigate to={encodeURIComponent(programs[0].partnerId)} replace />} />
      <Route path=":partnerId/*" element={<ProgramRoute programs={programs} capabilities={capabilities} />} />
    </Routes>
  );
};

export default MyReferralProgramPage;
